/**
 * A ref can outlive the async operation which set it (for example when an
 * account event invalidates that operation). It is only safe to reclaim it
 * when there is no UI-visible or wallet-return reconnect still in flight.
 */
export function shouldRecoverStaleConnectLock(state) {
    return state.locked && !state.visible && !state.reconnectInFlight;
}
// An explicit tap must not wait tens of seconds on an orphaned WC pairing.
export const EXPLICIT_PAIRING_RESTORE_ATTEMPTS = 2;
export async function completeFreshWalletBoundAuthorization(reconstructedData, refreshLiveWalletSession, commitFreshSession, issueBackendAuthorization, assertCurrent) {
    assertCurrent();
    const clientSessionToken = await refreshLiveWalletSession();
    assertCurrent();
    commitFreshSession(reconstructedData, clientSessionToken);
    assertCurrent();
    const result = await issueBackendAuthorization(reconstructedData);
    assertCurrent();
    return result;
}
export class StaleConnectionOperationError extends Error {
    constructor() {
        super("Wallet session changed during authentication.");
        this.name = "StaleConnectionOperationError";
    }
}
export function shouldClearRestoredWalletAuth(storedAuthWallet, liveWallet, hasWalletScopedCache) {
    const stored = storedAuthWallet?.toLowerCase();
    const live = liveWallet?.toLowerCase();
    return (!!stored && stored !== live) || (!stored && hasWalletScopedCache);
}
export function canRetryBackendAuth(status, onboardingStatus) {
    return status === "unavailable" && onboardingStatus === "error";
}
export function shouldClearWalletScopedDataAfterBackendFailure(status) {
    return status === 401 || status === 403;
}
export function transitionRestoredWalletAccount(guard, currentLiveSession, restoredAddress, storedAuthWallet, hasWalletScopedCache, clearWalletScopedCache, commitLiveSession) {
    const address = restoredAddress.trim().toLowerCase();
    if (!address)
        throw new Error("Cannot restore an empty wallet address.");
    const operation = guard.invalidate();
    const previousAddress = currentLiveSession.address?.trim().toLowerCase();
    const switchedAccount = currentLiveSession.isConnected
        && !!previousAddress
        && previousAddress !== address;
    const cacheCleared = switchedAccount || shouldClearRestoredWalletAuth(storedAuthWallet, address, hasWalletScopedCache);
    // Cache removal intentionally precedes publishing the new live account.
    // Publishing can trigger authentication effects synchronously in consumers.
    if (cacheCleared)
        clearWalletScopedCache();
    commitLiveSession(address);
    return { address, cacheCleared, operation };
}
export function createGuardedConnectionSession(guard, operation, expectedAddress, getLiveSession) {
    const normalizedExpectedAddress = expectedAddress.toLowerCase();
    const isCurrent = () => {
        const live = getLiveSession();
        return guard.isCurrent(operation)
            && live.isConnected
            && live.address?.toLowerCase() === normalizedExpectedAddress;
    };
    return {
        isCurrent,
        assertCurrent: () => {
            if (!isCurrent())
                throw new StaleConnectionOperationError();
        },
        commit: (callback) => {
            if (!isCurrent())
                return false;
            callback();
            return true;
        },
    };
}
export async function commitGuardedAsyncResult(result, session, callback) {
    const value = await result;
    return session.commit(() => callback(value));
}
export function createConnectionOperationGuard() {
    let generation = 0;
    return {
        begin: () => ++generation,
        current: () => generation,
        invalidate: () => ++generation,
        isCurrent: (operation) => operation === generation,
    };
}
